import type { ComponentProps, ReactNode } from 'react';
import { Loader2, Link as LinkIcon } from 'lucide-react';
import { useTranslate } from 'ra-core';
import { Button } from '../ui/button';
import { IconButtonWithTooltip } from '../admin/icon-button-with-tooltip';
import { useCopyGrowAppLink } from '../../hooks/useCopyGrowAppLink';

export interface CopyGrowAppLinkButtonProps {
  path: `/${string}`;
  label?: ReactNode;
  iconOnly?: boolean;
  className?: string;
  variant?: ComponentProps<typeof Button>['variant'];
  size?: ComponentProps<typeof Button>['size'];
}

/** Copies a relative Grow app path and reports the result to the user. */
export function CopyGrowAppLinkButton({
  path,
  label: labelProp,
  iconOnly = false,
  className,
  variant = 'outline',
  size,
}: CopyGrowAppLinkButtonProps) {
  const translate = useTranslate();
  const copyLink = useCopyGrowAppLink();
  const label =
    labelProp ?? translate('moonstar.growAppLink.copy', { _: 'Copy link' });

  const icon = copyLink.isPending ? (
    <Loader2 className="animate-spin" />
  ) : (
    <LinkIcon />
  );

  if (iconOnly) {
    return (
      <IconButtonWithTooltip
        label={label}
        onClick={() => copyLink.mutate(path)}
        disabled={copyLink.isPending}
        className={className}
      >
        {icon}
      </IconButtonWithTooltip>
    );
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      disabled={copyLink.isPending}
      onClick={() => copyLink.mutate(path)}
    >
      {icon}
      {label}
    </Button>
  );
}
